import { z } from "zod";

const nonEmptyStr = (field: string) =>
  z.string().trim().min(1, `${field} is required`).max(500, `${field} is too long`);

const emailStr = (field: string) =>
  z.string().trim().email(`${field} must be a valid email`).max(255);

const phoneStr = (field: string) =>
  z.string().trim().min(1, `${field} is required`).max(30, `${field} is too long`);

export const wpClaimSchema = z.object({
  user_id: z.string().uuid("Invalid user ID"),

  adjuster_name: nonEmptyStr("Adjuster name"),
  adjuster_email: emailStr("Adjuster email"),
  adjuster_phone: phoneStr("Adjuster phone"),
  adjuster_company: nonEmptyStr("Adjuster company"),
  adjuster_license: nonEmptyStr("Adjuster license"),

  claim_number: nonEmptyStr("Claim number"),
  loss_date: nonEmptyStr("Loss date"),
  claim_type: nonEmptyStr("Claim type"),

  policyholder_name: nonEmptyStr("Policyholder name"),
  policyholder_email: emailStr("Policyholder email"),
  policyholder_phone: phoneStr("Policyholder phone"),

  policyholder_address: nonEmptyStr("Policyholder address"),
  policyholder_city: nonEmptyStr("Policyholder city"),
  policyholder_state: nonEmptyStr("Policyholder state").max(2, "State must be a 2-letter code"),
  policyholder_zip: nonEmptyStr("Policyholder ZIP").max(10, "ZIP code is too long"),

  family_dynamics: z.string().max(5000).nullable().optional(),
  approved_terms: z.string().max(5000).nullable().optional(),

  submitted_by: nonEmptyStr("Submitted by"),
  submitted_at: z.string().min(1, "Submitted date is required"),
});

export type ValidatedWpClaim = z.infer<typeof wpClaimSchema>;

export interface WpClaimValidationResult {
  success: boolean;
  data?: ValidatedWpClaim;
  errors?: { field: string; message: string }[];
}

export function validateWpClaim(payload: Record<string, any>): WpClaimValidationResult {
  const result = wpClaimSchema.safeParse(payload);

  if (result.success) {
    return { success: true, data: result.data };
  }

  const errors = result.error.issues.map((issue) => ({
    field: issue.path.join("."),
    message: issue.message,
  }));

  return { success: false, errors };
}

export function formatValidationErrors(errors: { field: string; message: string }[]): string {
  if (errors.length === 1) return errors[0].message;
  return `${errors.length} validation errors:\n${errors.map((e) => `• ${e.message}`).join("\n")}`;
}
